import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Clock, User, ArrowLeft, Info } from "lucide-react";
import { SealCheck } from "@phosphor-icons/react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { FoundingBadge } from "@/components/FoundingBadge";
import { RatingBadge, RatingStars } from "@/components/RatingStars";
import { CATEGORY_META, CURRENCY_SYMBOL } from "@/data/categories";
import { priceLabel, fmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const timeOptions = Array.from({ length: 27 }, (_, i) => {
  const h = 7 + Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, tc, td } = useLang();
  const [listing, setListing] = useState(null);
  const [booking, setBooking] = useState({ date: "", start_time: "09:00", end_time: "12:00", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => navigate("/browse"));
  }, [id, navigate]);

  if (!listing) return <div className="grid min-h-[50vh] place-items-center text-ppc-muted">{t("loading")}</div>;

  const meta = CATEGORY_META[listing.category] || {};
  const price = priceLabel(listing, t);
  const sym = CURRENCY_SYMBOL[listing.currency] || listing.currency + " ";

  const hours = (() => {
    const [sh, sm] = booking.start_time.split(":").map(Number);
    const [eh, em] = booking.end_time.split(":").map(Number);
    const diff = eh + em / 60 - (sh + sm / 60);
    return diff > 0 ? diff : 0;
  })();

  const estTotal =
    listing.price_type === "fixed"
      ? Number(listing.price_fixed || 0)
      : (((listing.price_min || 0) + (listing.price_max || listing.price_min || 0)) / 2) * (hours || 1);

  const bookingFee = Math.round(estTotal * 10) / 100;
  const grandTotal = Math.round(((estTotal + bookingFee + 0.25) / 0.985) * 100) / 100;
  const stripeFee = Math.round((grandTotal - estTotal - bookingFee) * 100) / 100;

  const submitBooking = async () => {
    if (!user) {
      toast.error(t("toast_login_first"));
      navigate("/login", { state: { from: `/listing/${id}` } });
      return;
    }
    if (!booking.date) {
      toast.error(t("book_date"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/bookings", {
        listing_id: listing.id,
        date: booking.date,
        start_time: booking.start_time,
        end_time: booking.end_time,
        hours,
        message: booking.message,
      });
      toast.success(t("toast_booking_sent"));
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 pb-28 pt-8 md:pb-8 lg:px-8">
      {/* Mobile hero */}
      <div className="lg:hidden" data-testid="detail-hero-mobile">
        <div className="detail-hero h-[280px]">
          <img src={listing.image || meta.image} alt={listing.title} />
          <span className="detail-hero-veil" aria-hidden="true" />
          <button
            onClick={() => navigate(-1)}
            data-testid="detail-back-mobile"
            className="glass-pill absolute left-3.5 top-3.5 grid h-10 w-10 place-items-center !rounded-2xl !p-0"
            aria-label={t("nav_browse")}
          >
            <ArrowLeft className="h-4 w-4 text-ppc-text" />
          </button>
          <span className="glass-pill absolute right-3.5 top-3.5 text-[12.5px] text-ppc-text">
            {tc(listing.category)}
          </span>
          <div className="absolute inset-x-3.5 bottom-3.5 flex gap-2">
            <span className="plan-pill">
              <span className="label-caps block">{t("est_service")}</span>
              <span className="text-[14px] font-medium text-ppc-text">{fmt(sym, estTotal)}</span>
            </span>
            <span className="plan-pill">
              <span className="label-caps block">{t("fee_label")}</span>
              <span className="text-[14px] font-medium text-ppc-text">{fmt(sym, bookingFee)}</span>
            </span>
            <span className="plan-pill plan-pill-accent">
              <span className="label-caps block">{t("est_total")}</span>
              <span className="text-[14px] font-medium text-primary-green">{fmt(sym, grandTotal)}</span>
            </span>
          </div>
        </div>
      </div>

      <button
        onClick={() => navigate(-1)}
        data-testid="listing-back-btn"
        className="mb-6 hidden items-center gap-1.5 text-sm text-ppc-muted hover:text-primary-green lg:flex"
      >
        <ArrowLeft className="h-4 w-4" /> {t("nav_browse")}
      </button>

      <div className="grid gap-10 lg:grid-cols-5">
        {/* Left */}
        <div className="lg:col-span-3">
          <div className="hidden overflow-hidden rounded-3xl border border-ppc lg:block">
            <img src={listing.image || meta.image} alt={listing.title} className="h-72 w-full object-cover" />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 lg:mt-6">
            <Badge className="rounded-xl st-accepted hover:st-accepted lg:inline-flex hidden">
              {tc(listing.category)}
            </Badge>
            {listing.is_business && (
              <Badge className="rounded-xl st-pending">
                {t("business_badge")}
              </Badge>
            )}
          </div>
          <h1 className="font-display mt-4 text-4xl font-medium tracking-tighter text-ppc-text">
            {listing.title}
          </h1>
          <p className="mt-3 flex items-center gap-1.5 text-ppc-muted">
            <MapPin className="h-4 w-4" /> {listing.city}, {listing.country}
          </p>
          <p className="mt-6 whitespace-pre-line leading-relaxed text-ppc-text">{listing.description}</p>

          {/* Provider */}
          <div className="mt-8 rounded-3xl border border-ppc bg-ppc-surface p-6">
            <h3 className="font-display text-lg font-medium text-ppc-text">{t("about_provider")}</h3>
            <div className="mt-3 flex items-center gap-3">
              <span className="thumb-glass grid h-12 w-12 shrink-0 place-items-center">
                {listing.provider_photo ? (
                  <img src={`${API}${listing.provider_photo.replace("/api", "")}`} alt={listing.provider_name} />
                ) : (
                  <User className="h-5 w-5 text-ppc-text" />
                )}
              </span>
              <div>
                <p className="flex items-center gap-1.5 font-medium text-ppc-text">
                  {listing.provider_name}
                  {listing.provider_verified && <SealCheck size={15} weight="fill" color="#9184d9" />}
                </p>
                {listing.provider_verified && (
                  <p className="mt-0.5 text-[12px] text-primary-green" data-testid="provider-verified-badge">{t("ver_badge")}</p>
                )}
                {listing.rating_count > 0 && <RatingBadge avg={listing.rating} count={listing.rating_count} className="mt-1" />}
                <FoundingBadge fm={listing.founding_member} size="lg" className="mt-1.5" />
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-6 rounded-3xl border border-ppc bg-ppc-surface p-6" data-testid="reviews-section">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-medium text-ppc-text">{t("rev_title")}</h3>
              {listing.rating_count > 0 && <RatingBadge avg={listing.rating} count={listing.rating_count} />}
            </div>
            {!listing.reviews?.length ? (
              <p className="mt-3 text-sm text-ppc-muted" data-testid="reviews-empty">{t("rev_none")}</p>
            ) : (
              <div className="mt-4 space-y-3">
                {listing.reviews.map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl bg-ppc-bg p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-ppc-text">{r.customer_name}</p>
                      <RatingStars value={r.rating} />
                    </div>
                    {r.comment && <p className="mt-1.5 text-[13px] leading-relaxed text-ppc-muted">{r.comment}</p>}
                    <p className="mt-1 text-[11px] text-ppc-muted">{r.created_at?.slice(0, 10)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Availability */}
          {listing.availability?.length > 0 && (
            <div className="mt-6 rounded-3xl border border-ppc bg-ppc-surface p-6">
              <h3 className="font-display flex items-center gap-2 text-lg font-medium text-ppc-text">
                <Clock className="h-5 w-5" /> {t("availability_title")}
              </h3>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {listing.availability.map((slot, i) => (
                  <div
                    key={i}
                    data-testid={`availability-slot-${i}`}
                    className="flex items-center justify-between rounded-xl bg-ppc-bg px-4 py-2 text-sm"
                  >
                    <span className="font-medium text-ppc-text">{td(slot.day)}</span>
                    <span className="text-ppc-muted">
                      {slot.start} – {slot.end}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Booking box */}
        <div className="lg:col-span-2" id="booking-box">
          <div className="sticky top-24 rounded-3xl border border-ppc bg-ppc-surface p-6">
            <div className="flex items-baseline gap-1">
              <span className="font-display text-3xl font-medium text-primary-green">
                {price.main}
              </span>
              <span className="text-sm text-ppc-muted">{price.unit}</span>
            </div>

            <h3 className="font-display mt-6 text-lg font-medium text-ppc-text">{t("book_title")}</h3>
            <div className="mt-4 space-y-4">
              <div>
                <Label htmlFor="date">{t("book_date")}</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="booking-date-input"
                  value={booking.date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setBooking((b) => ({ ...b, date: e.target.value }))}
                  className="mt-1.5 rounded-xl border-ppc bg-ppc-bg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("book_start")}</Label>
                  <Select
                    value={booking.start_time}
                    onValueChange={(v) => setBooking((b) => ({ ...b, start_time: v }))}
                  >
                    <SelectTrigger data-testid="booking-start-select" className="mt-1.5 rounded-xl border-ppc bg-ppc-bg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="">
                      {timeOptions.map((tm) => (
                        <SelectItem key={tm} value={tm}>
                          {tm}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("book_end")}</Label>
                  <Select
                    value={booking.end_time}
                    onValueChange={(v) => setBooking((b) => ({ ...b, end_time: v }))}
                  >
                    <SelectTrigger data-testid="booking-end-select" className="mt-1.5 rounded-xl border-ppc bg-ppc-bg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="">
                      {timeOptions.map((tm) => (
                        <SelectItem key={tm} value={tm}>
                          {tm}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="msg">{t("book_message")}</Label>
                <Textarea
                  id="msg"
                  data-testid="booking-message-input"
                  value={booking.message}
                  onChange={(e) => setBooking((b) => ({ ...b, message: e.target.value }))}
                  placeholder={t("book_message_ph")}
                  className="mt-1.5 rounded-xl border-ppc bg-ppc-bg"
                />
              </div>

              <div className="rounded-2xl border border-ppc bg-ppc-bg px-4 py-3 text-sm" data-testid="booking-breakdown">
                <div className="flex justify-between text-ppc-muted"><span>{t("est_service")}</span><span className="text-ppc-text">{fmt(sym, estTotal)}</span></div>
                <div className="mt-1 flex justify-between text-ppc-muted">
                  <span>{t("fee_label")} · 10%</span>
                  <span className="text-ppc-text">{fmt(sym, bookingFee)}</span>
                </div>
                <div className="mt-1 flex justify-between text-ppc-muted"><span>{t("fee_processing_note")}</span><span className="text-ppc-text">{fmt(sym, stripeFee)}</span></div>
                <div className="mt-2 flex items-center justify-between border-t border-ppc pt-2">
                  <span className="font-medium text-ppc-text">{t("est_total")}</span>
                  <span className="font-display text-xl text-primary-green" data-testid="booking-est-total">{fmt(sym, grandTotal)}</span>
                </div>
              </div>
              <p className="flex items-start gap-1.5 text-xs text-ppc-muted" data-testid="booking-fee-note">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t("fee_provider_note")}
              </p>

              <Button
                onClick={submitBooking}
                disabled={busy}
                data-testid="booking-submit-btn"
                className="w-full rounded-xl btn-primary"
              >
                {user ? t("book_submit") : t("book_login")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile book bar */}
      <div className="book-bar lg:hidden" data-testid="mobile-book-bar">
        <button
          onClick={() => document.getElementById("booking-box")?.scrollIntoView({ behavior: "smooth" })}
          data-testid="mobile-book-now-btn"
          className="btn-primary h-12 flex-1 rounded-2xl text-[14px]"
        >
          {user ? t("book_submit") : t("book_login")}
        </button>
        <span className="glass-pill !rounded-2xl px-4 py-2.5 text-[14px] font-medium text-ppc-text">
          {fmt(sym, grandTotal)}
        </span>
      </div>
    </div>
  );
}
